<template>
  <component
    :is="iconComponent"
    :class="iconClasses"
    :style="iconStyles"
    :aria-label="ariaLabel"
    :aria-hidden="!ariaLabel"
  />
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue'
import { getIconComponent } from '../utils/iconRegistry'

interface Props {
  name: string
  variant?: 'outline' | 'solid'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
  color?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'outline',
  size: 'md',
  color: 'currentColor'
})

const iconComponent = computed<Component | null>(() => {
  return getIconComponent(props.name, props.variant)
})

const iconClasses = computed(() => [
  'icon',
  `icon--${props.size}`,
  {
    'icon--outline': props.variant === 'outline',
    'icon--solid': props.variant === 'solid'
  }
])

const iconStyles = computed(() => {
  const styles: Record<string, string> = {}
  
  if (props.color && props.color !== 'currentColor') {
    styles.color = props.color
  }
  
  if (typeof props.size === 'number') {
    styles.width = `${props.size}px`
    styles.height = `${props.size}px`
  }
  
  return styles
})

const ariaLabel = computed(() => props.ariaLabel)
</script>

<style scoped>
.icon {
  display: inline-block;
  flex-shrink: 0;
  vertical-align: middle;
  color: currentColor;
}

/* Size variants */
.icon--xs {
  width: 12px;
  height: 12px;
}

.icon--sm {
  width: 16px;
  height: 16px;
}

.icon--md {
  width: 20px;
  height: 20px;
}

.icon--lg {
  width: 24px;
  height: 24px;
}

.icon--xl {
  width: 32px;
  height: 32px;
}

/* Ensure icons inherit color from parent */
.icon :deep(svg) {
  width: 100%;
  height: 100%;
  color: inherit;
  fill: currentColor;
  stroke: currentColor;
}
</style>
